import { Fragment, useRef, useState } from 'react'
import type { KeyboardEvent, ClipboardEvent, ChangeEvent } from 'react'
import { motion, useReducedMotion } from 'motion/react'

interface OTPInputProps {
  onComplete: (value: string) => void
  disabled?: boolean
}

export function OTPInput({ onComplete, disabled = false }: OTPInputProps) {
  const [values, setValues] = useState<string[]>(Array(6).fill(''))
  const [focused, setFocused] = useState<number | null>(null)
  const inputRefs = useRef<(HTMLInputElement | null)[]>(Array(6).fill(null))
  const shouldReduceMotion = useReducedMotion()

  const focusBox = (index: number) => {
    inputRefs.current[index]?.focus()
  }

  const handleChange = (index: number, e: ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '')
    if (!raw) return

    const char = raw[raw.length - 1]
    const newValues = [...values]
    newValues[index] = char
    setValues(newValues)

    const filled = newValues.join('')
    if (filled.length === 6) {
      onComplete(filled)
    } else if (index < 5) {
      focusBox(index + 1)
    }
  }

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      e.preventDefault()
      const newValues = [...values]
      if (values[index]) {
        newValues[index] = ''
        setValues(newValues)
      } else if (index > 0) {
        newValues[index - 1] = ''
        setValues(newValues)
        focusBox(index - 1)
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      focusBox(index - 1)
    } else if (e.key === 'ArrowRight' && index < 5) {
      focusBox(index + 1)
    }
  }

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault()
    const pasted = e.clipboardData.getData('text').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6)
    if (!pasted) return

    const newValues = [...values]
    for (let i = 0; i < 6; i++) {
      newValues[i] = pasted[i] ?? ''
    }
    setValues(newValues)

    const lastFilled = Math.min(pasted.length, 5)
    focusBox(lastFilled)

    if (pasted.length === 6) {
      onComplete(pasted)
    }
  }

  const springTransition = shouldReduceMotion
    ? { duration: 0 }
    : { type: 'spring' as const, stiffness: 300, damping: 24 }

  return (
    <div
      style={{
        display: 'flex',
        gap: 'var(--otp-box-gap)',
        width: '100%',
        alignItems: 'center',
      }}
      role="group"
      aria-label="Verification code input"
    >
      {values.map((val, index) => {
        const isFocused = focused === index
        const hasValue = Boolean(val)

        return (
          <Fragment key={index}>
            <motion.div
              initial={false}
              animate={
                hasValue
                  ? shouldReduceMotion
                    ? { scale: 1 }
                    : { scale: [0.8, 1] }
                  : { scale: 1 }
              }
              transition={springTransition}
              style={{ display: 'flex', flex: 1 }}
            >
              <input
                ref={(el) => { inputRefs.current[index] = el }}
                type="text"
                inputMode="text"
                autoComplete="one-time-code"
                maxLength={2}
                value={val}
                disabled={disabled}
                aria-label={`Character ${index + 1} of 6`}
                onChange={(e) => handleChange(index, e)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={handlePaste}
                onFocus={() => setFocused(index)}
                onBlur={() => setFocused(null)}
                style={{
                  width: '100%',
                  minWidth: 0,
                  height: 'var(--otp-box-height)',
                  textAlign: 'center',
                  fontFamily: 'var(--font-display)',
                  fontSize: 'var(--text-title-md)',
                  fontWeight: 'var(--weight-bold)',
                  color: 'var(--color-text-primary)',
                  background: 'var(--color-surface-container-lowest)',
                  border: isFocused
                    ? 'var(--border-field-width) solid var(--color-outline-focus)'
                    : 'var(--border-field-width) solid var(--color-field-border)',
                  borderRadius: 'var(--radius-md)',
                  outline: 'none',
                  boxShadow: isFocused ? 'var(--shadow-focus-ring)' : 'none',
                  cursor: disabled ? 'not-allowed' : 'text',
                  opacity: disabled ? 0.5 : 1,
                  transition: 'border-color 150ms ease, box-shadow 150ms ease',
                  caretColor: 'transparent',
                }}
              />
            </motion.div>
            {index === 2 && (
              <span
                aria-hidden="true"
                style={{
                  flex: 'none',
                  width: '10px',
                  height: '2px',
                  borderRadius: '1px',
                  background: 'var(--color-field-border)',
                }}
              />
            )}
          </Fragment>
        )
      })}
    </div>
  )
}
